/**
 * Locale-switching URL helpers for the language picker.
 *
 * All functions are pure: callers pass the current pathname/search rather than
 * having helpers read `window.location` directly. This keeps server and client
 * render output consistent and avoids React hydration mismatches.
 */
/** Mons locale query parameter key — single source of truth for locale-switching URLs */
export const LOCALE_PARAM = 'mons_sel_locale';

/**
 * Build the URL query string with the locale param set, preserving any other
 * existing query parameters from `currentSearch`. Returns the bare
 * `key=value&...` payload (no leading `?`).
 *
 * Pure function — caller is responsible for sourcing `currentSearch`. Pass an
 * empty string when no existing search params should be preserved (e.g. SSR
 * or first render before client URL state is hydrated).
 */
function buildLocaleQuery(locale: string, currentSearch: string): string {
  const params = new URLSearchParams(currentSearch);
  params.set(LOCALE_PARAM, locale);
  return params.toString();
}

/**
 * Build a locale-switching URL.
 *
 * Pure function — caller MUST pass the current pathname and search from a
 * source that's consistent between server and client render to avoid
 * hydration mismatches (typically: empty strings during SSR / first render,
 * then `window.location.pathname` / `.search` after mount).
 *
 * @param langCode - Target language code (e.g. 'en-US', 'es')
 * @param currentLanguage - Currently active language code
 * @param addPathLocale - Whether to add locale prefix to the URL path
 * @param currentPathname - Current pathname (e.g. '/products') — pass '' for SSR
 * @param currentSearch - Current search string (e.g. '?ref=foo') — pass '' for SSR
 */
export function buildLanguageUrl(
  langCode: string,
  currentLanguage: string,
  addPathLocale: boolean,
  currentPathname: string = '',
  currentSearch: string = ''
): string {
  const locale = langCode.replace(/-/g, '_');
  const query = buildLocaleQuery(locale, currentSearch);

  // Path-locale branch: prepend the BCP-47 prefix (e.g. /es) when switching
  // to a different language. Used by sites like SellDot that author with
  // addPathLocale=true.
  if (addPathLocale && langCode !== currentLanguage) {
    const bcp47 = langCode.split('-')[0];
    return `/${bcp47}?${query}`;
  }

  // Param-only branch: keep the current pathname, just update the locale
  // query param. Used by Seller Central and same-language switches.
  return `${currentPathname}?${query}`;
}

/**
 * Detect the active locale from a URL query string.
 *
 * Pure function — caller MUST pass `currentSearch` from a source that's
 * consistent between server and client render. Pass empty string during SSR
 * or first render to defer detection until after mount.
 *
 * Reads the `mons_sel_locale` param (e.g. `es_US`) and matches it against
 * the available language codes (e.g. `es-US`). Returns the matching language
 * code or undefined if no match is found.
 *
 * The URL param uses underscores (`es_US`) while language codes use hyphens
 * (`es-US`), so we normalize before comparison.
 *
 * @param languageCodes - Array of available language codes (e.g. ['en-US', 'es-US'])
 * @param currentSearch - URL search string (e.g. '?mons_sel_locale=es_US')
 * @returns Matching language code or undefined
 */
export function detectLocaleFromUrl(
  languageCodes: string[],
  currentSearch: string = ''
): string | undefined {
  if (!currentSearch) return undefined;
  const params = new URLSearchParams(currentSearch);
  const urlLocale = params.get(LOCALE_PARAM);
  if (!urlLocale) return undefined;

  // Normalize: URL uses underscores (es_US), codes use hyphens (es-US)
  const normalized = urlLocale.replace(/_/g, '-');

  // Exact match (case-insensitive)
  const match = languageCodes.find(
    code => code.toLowerCase() === normalized.toLowerCase()
  );
  if (match) return match;

  // Fallback: match by BCP-47 prefix (e.g. "es" matches "es-US")
  const prefix = normalized.split('-')[0].toLowerCase();
  return languageCodes.find(
    code => code.split('-')[0].toLowerCase() === prefix
  );
}
